"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useSession } from "next-auth/react";

import {
  clearCartTtl,
  isCartOrderExpired,
  readCartTouchedAt,
  touchCartTtl,
} from "~/lib/cart-ttl";
import { handleUnauthorizedSession } from "~/lib/client-session";
import {
  createCartOrder,
  getCartOrder,
  updateCartItemStatus,
  updateCartOrder,
  type CartOrder as ApiCartOrder,
  type CartOrderProduct,
} from "~/network/api";

const GUEST_CART_KEY = "dido-guest-cart";

/** Variant metadata persisted on the line item and sent with order sync. */
export interface CartItemAttribute {
  id: number;
  price: number;
  /** Attribute key from API, e.g. "color". */
  name?: string;
  /** Attribute value, e.g. "Brown". */
  value?: string;
  /** Primary attribute image URL when available. */
  image?: string;
}

export interface CartItem {
  /** Stable unique key for UI/state updates (backend line id when available). */
  key: string;
  /** Backend order id this item was hydrated from (when available). */
  orderId?: string;
  /** Backend cart-line id (used for delete/update line status) */
  id?: string;
  productId: string;
  name: string;
  price: string;
  image: string;
  quantity: number;
  /** When set, used for order sync (e.g. from API product or hydrated order) */
  unitBasePrice?: number;
  /** From product data; used for cart order sync */
  businessId?: string;
  /** Selected product attribute (variant); `price` is unit price matching `unitBasePrice`. */
  attribute?: CartItemAttribute;
  /**
   * Max units allowed for this line (from `Product.quantity` or attribute `quantity` at add).
   * Always stored as a number when present so the cart UI can cap +/sync.
   */
  maxQuantity?: number;
}

export type AddCartItemInput = Omit<CartItem, "quantity" | "key"> & {
  key?: string;
  unitBasePrice?: number;
  businessId?: string;
};

interface CartContextValue {
  items: CartItem[];
  isOpen: boolean;
  openCart: () => void;
  closeCart: () => void;
  addItem: (item: AddCartItemInput, quantity?: number) => void;
  /**
   * When signed in, updates local cart then immediately syncs to customer-orders
   * (products array includes `attribute: { id, quantity, price, name?, value?, image? }` for variants).
   */
  addItemAndSyncToServer: (
    item: AddCartItemInput,
    quantity?: number,
  ) => Promise<void>;
  updateQuantity: (itemKey: string, quantity: number) => void;
  removeItem: (itemKey: string) => void;
  clearCart: (options?: { skipServerDelete?: boolean }) => void;
  totalCount: number;
  totalPrice: string;
  /** True while loading cart from API or syncing */
  syncing: boolean;
  /** Clears pending debounced sync, runs server sync immediately, returns cart lines to use (e.g. checkout). */
  flushCartSync: () => Promise<CartItem[]>;
}

const CartContext = createContext<CartContextValue | null>(null);

function parsePrice(s: string): number {
  const n = parseFloat(s.replace(/[^0-9.]/g, ""));
  return Number.isNaN(n) ? 0 : n;
}

function formatPrice(n: number): string {
  return `$${n.toFixed(2)}`;
}

/** Coerce line `maxQuantity` (number or string from JSON) for caps and UI. */
export function maxQuantityForCartLine(raw: unknown): number | undefined {
  if (raw == null) return undefined;
  if (typeof raw === "number" && Number.isFinite(raw) && raw >= 0) {
    return Math.floor(raw);
  }
  if (typeof raw === "string") {
    const n = parseInt(raw.trim(), 10);
    if (Number.isFinite(n) && n >= 0) return n;
  }
  return undefined;
}

function coerceAttrInt(raw: unknown): number | undefined {
  if (typeof raw === "number" && Number.isFinite(raw)) return Math.round(raw);
  if (typeof raw === "string" && /^\d+$/.test(raw.trim())) {
    return parseInt(raw.trim(), 10);
  }
  return undefined;
}

function coerceAttrPrice(raw: unknown): number | undefined {
  if (typeof raw === "number" && Number.isFinite(raw)) return Math.round(raw);
  if (typeof raw === "string") {
    const n = parseFloat(raw.trim());
    if (Number.isFinite(n)) return Math.round(n);
  }
  return undefined;
}

function attributeFromOrderProduct(p: CartOrderProduct): CartItemAttribute | undefined {
  const a = p.attribute;
  if (!a || typeof a !== "object" || Array.isArray(a)) return undefined;
  const rec = a as {
    id?: unknown;
    price?: unknown;
    name?: unknown;
    value?: unknown;
    image?: unknown;
  };
  const id = coerceAttrInt(rec.id);
  const price = coerceAttrPrice(rec.price);
  if (id === undefined || price === undefined) return undefined;
  const name = typeof rec.name === "string" && rec.name.trim() ? rec.name.trim() : undefined;
  const value = typeof rec.value === "string" && rec.value.trim() ? rec.value.trim() : undefined;
  const image = typeof rec.image === "string" && rec.image.trim() ? rec.image.trim() : undefined;
  return {
    id,
    price,
    ...(name ? { name } : {}),
    ...(value ? { value } : {}),
    ...(image ? { image } : {}),
  };
}

/** Same idea as customer-products `coerceProductId` — cart order lines vary by field. */
function productIdFromOrderLine(p: CartOrderProduct): string {
  const r = p as unknown as Record<string, unknown>;
  const candidates: unknown[] = [
    p.product_id,
    p.id,
    r.productId,
    r.uuid,
  ];
  for (const c of candidates) {
    if (typeof c === "string") {
      const t = c.trim();
      if (t.length > 0) return t;
    }
    if (typeof c === "number" && Number.isFinite(c)) {
      return String(Math.trunc(c));
    }
  }
  return "";
}

function linesFromCartOrder(order: ApiCartOrder): CartOrderProduct[] {
  const o = order as Record<string, unknown>;
  const raw =
    order.products ??
    o.items ??
    o.lines ??
    o.order_items ??
    o.orderProducts;
  return Array.isArray(raw) ? (raw as CartOrderProduct[]) : [];
}

/** Backend order id may be string or number; normalize for comparisons and API calls. */
function normalizeOrderIdForCart(raw: unknown): string {
  if (raw == null) return "";
  return String(raw).trim();
}

function countOtherLinesInSameOrder(
  lines: CartItem[],
  orderIdNorm: string,
  excludingKey: string,
): number {
  if (!orderIdNorm) return 0;
  return lines.filter(
    (i) => normalizeOrderIdForCart(i.orderId) === orderIdNorm && i.key !== excludingKey,
  ).length;
}

/** Attach server `orderId` / line `id` to local lines (same product + variant). */
function mergeOrderIdsFromServerLines(
  local: CartItem[],
  serverLines: CartItem[],
): { merged: CartItem[]; changed: boolean } {
  const matchKey = (i: CartItem) => `${i.productId}:${i.attribute?.id ?? ""}`;
  const byMatch = new Map(serverLines.map((s) => [matchKey(s), s] as const));
  let changed = false;
  const merged = local.map((i) => {
    const s = byMatch.get(matchKey(i));
    if (!s?.orderId) return i;
    const nextId = s.id ?? i.id;
    if (i.orderId !== s.orderId || i.id !== nextId) {
      changed = true;
      return { ...i, orderId: s.orderId, id: nextId };
    }
    return i;
  });
  return { merged, changed };
}

/** After POST /customer-orders, map new cart `order.id` onto lines for that `business_id`. */
function mergeCreatedOrderIds(
  local: CartItem[],
  businessIdToOrderId: Map<string, string>,
): { merged: CartItem[]; changed: boolean } {
  let changed = false;
  const merged = local.map((i) => {
    if (i.orderId) return i;
    const oid = i.businessId ? businessIdToOrderId.get(i.businessId) : undefined;
    if (!oid) return i;
    changed = true;
    return { ...i, orderId: oid, id: i.productId };
  });
  return { merged, changed };
}

function orderIdFromCreateResult(
  res: Awaited<ReturnType<typeof createCartOrder>>,
): string | undefined {
  if (!res.ok || !res.order) return undefined;
  const id = res.order.id;
  if (typeof id === "string" && id.trim()) return id.trim();
  if (typeof id === "number" && Number.isFinite(id)) return String(Math.trunc(id));
  return undefined;
}

function orderProductsToItems(order: ApiCartOrder | null): CartItem[] {
  if (!order) return [];
  const lines = linesFromCartOrder(order);
  if (lines.length === 0) return [];
  const placeholder =
    "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='64' height='64'%3E%3Crect fill='%23f5f5f4' width='64' height='64'/%3E%3C/svg%3E";
  const out: CartItem[] = [];
  for (const p of lines) {
    const productId = productIdFromOrderLine(p);
    if (!productId) continue;
    const businessId = p.business_id ?? order.business_id;
    const attribute = attributeFromOrderProduct(p);
    const key = `${order.id}:${productId}${attribute ? `:a:${attribute.id}` : ""}`;
    out.push({
      key,
      orderId: order.id,
      id: productId,
      productId,
      name: p.name ?? "Product",
      price: formatPrice(p.unit_base_price ?? 0),
      image: p.image ?? placeholder,
      quantity: p.quantity,
      unitBasePrice:
        typeof p.unit_base_price === "number"
          ? p.unit_base_price
          : Math.round(Number(p.unit_base_price)) || 0,
      businessId,
      ...(attribute ? { attribute } : {}),
    });
  }
  return out;
}

function ordersToItems(orders: ApiCartOrder[]): CartItem[] {
  const all: CartItem[] = [];
  for (const order of orders) {
    all.push(...orderProductsToItems(order));
  }
  return all;
}

function itemsToOrderProducts(items: CartItem[]): Array<{
  id: string;
  quantity: number;
  unit_base_price: number;
  image?: string;
  name?: string;
  attribute?: {
    id: number;
    quantity: number;
    price: number;
    name?: string;
    value?: string;
    image?: string;
  };
}> {
  return items.map((i) => ({
    id: i.productId,
    quantity: i.quantity,
    unit_base_price: i.unitBasePrice ?? Math.round(parsePrice(i.price)),
    image: i.image,
    name: i.name,
    ...(i.attribute
      ? {
          attribute: {
            id: i.attribute.id,
            quantity: i.quantity,
            price: i.attribute.price,
            ...(i.attribute.name ? { name: i.attribute.name } : {}),
            ...(i.attribute.value ? { value: i.attribute.value } : {}),
            ...(i.attribute.image ? { image: i.attribute.image } : {}),
          },
        }
      : {}),
  }));
}

function itemsTotalCents(items: CartItem[]): number {
  return items.reduce(
    (sum, i) => sum + (i.unitBasePrice ?? Math.round(parsePrice(i.price))) * i.quantity,
    0
  );
}

/** Stable payload identity for skipping redundant PATCH after server hydrate or sync. */
function cartItemsSyncFingerprint(items: CartItem[]): string {
  const normalized = [...items]
    .map((i) => ({
      productId: i.productId,
      quantity: i.quantity,
      unitBasePrice: i.unitBasePrice ?? Math.round(parsePrice(i.price)),
      orderId: i.orderId ?? "",
      businessId: i.businessId ?? "",
      attribute: i.attribute
        ? {
            id: i.attribute.id,
            price: i.attribute.price,
            name: i.attribute.name,
            value: i.attribute.value,
            image: i.attribute.image,
          }
        : undefined,
    }))
    .sort((a, b) => {
      const o = a.orderId.localeCompare(b.orderId);
      if (o !== 0) return o;
      const p = a.productId.localeCompare(b.productId);
      if (p !== 0) return p;
      return (a.attribute?.id ?? 0) - (b.attribute?.id ?? 0);
    });
  return JSON.stringify(normalized);
}

/** Ensures line quantities never exceed `maxQuantity` (e.g. after restoring guest cart). */
function clampCartItemQuantities(items: CartItem[]): CartItem[] {
  let changed = false;
  const next = items.map((i) => {
    const cap = maxQuantityForCartLine(i.maxQuantity);
    if (cap == null) return i;
    if (i.quantity <= cap) {
      if (typeof i.maxQuantity === "number" && i.maxQuantity === cap) return i;
      changed = true;
      return { ...i, maxQuantity: cap };
    }
    changed = true;
    return { ...i, quantity: cap, maxQuantity: cap };
  });
  return changed ? next : items;
}

function appendCartLineToItems(
  prev: CartItem[],
  item: AddCartItemInput,
  quantity: number,
): CartItem[] | null {
  const defaultKey = `${item.businessId ?? "unknown"}:${item.productId}${
    item.attribute ? `:a:${item.attribute.id}` : ""
  }`;
  const key = item.key ?? defaultKey;
  const existing = prev.find((i) => i.key === key);
  const resolvedCap = maxQuantityForCartLine(
    item.maxQuantity ?? existing?.maxQuantity,
  );
  const maxCap =
    resolvedCap != null ? resolvedCap : Number.POSITIVE_INFINITY;
  const existingQty = existing?.quantity ?? 0;
  const room =
    maxCap === Number.POSITIVE_INFINITY
      ? quantity
      : Math.max(0, maxCap - existingQty);
  const addQty = Math.min(quantity, room);
  if (addQty <= 0) return null;

  const mergedMax = maxQuantityForCartLine(
    item.maxQuantity ?? existing?.maxQuantity,
  );
  const { maxQuantity: _ignoredIncomingMax, ...itemWithoutMax } = item;

  if (existing) {
    return prev.map((i) =>
      i.key === key
        ? {
            ...i,
            quantity: i.quantity + addQty,
            unitBasePrice: item.unitBasePrice ?? i.unitBasePrice,
            businessId: item.businessId ?? i.businessId,
            ...(item.attribute ? { attribute: item.attribute } : {}),
            ...(mergedMax != null ? { maxQuantity: mergedMax } : {}),
          }
        : i,
    );
  }
  return [
    ...prev,
    {
      ...itemWithoutMax,
      key,
      quantity: addQty,
      unitBasePrice: item.unitBasePrice,
      businessId: item.businessId,
      ...(item.attribute ? { attribute: item.attribute } : {}),
      ...(mergedMax != null ? { maxQuantity: mergedMax } : {}),
    },
  ];
}

function cartLineIdentity(item: Pick<CartItem, "productId" | "attribute">): string {
  return `${item.productId}:${item.attribute?.id ?? ""}`;
}

function readGuestCartFromStorage(): CartItem[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = sessionStorage.getItem(GUEST_CART_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as CartItem[];
    return Array.isArray(parsed) ? clampCartItemQuantities(parsed) : [];
  } catch {
    return [];
  }
}

function mergeGuestCartWithServer(
  serverItems: CartItem[],
  guestItems: CartItem[],
): CartItem[] {
  if (guestItems.length === 0) return serverItems;
  if (serverItems.length === 0) return guestItems;

  const merged = [...serverItems];
  const indexByIdentity = new Map(
    merged.map((item, index) => [cartLineIdentity(item), index] as const),
  );

  for (const guest of guestItems) {
    const identity = cartLineIdentity(guest);
    const existingIndex = indexByIdentity.get(identity);
    if (existingIndex != null) {
      const existing = merged[existingIndex]!;
      // Server line already persisted — guest snapshot may be stale after a prior sync.
      if (existing.orderId) continue;
      const cap = maxQuantityForCartLine(guest.maxQuantity ?? existing.maxQuantity);
      const maxQty =
        cap != null ? cap : Number.POSITIVE_INFINITY;
      const nextQuantity = Math.min(maxQty, existing.quantity + guest.quantity);
      if (nextQuantity !== existing.quantity) {
        merged[existingIndex] = {
          ...existing,
          quantity: nextQuantity,
          ...(cap != null ? { maxQuantity: cap } : {}),
        };
      }
      continue;
    }

    const defaultKey = `${guest.businessId ?? "unknown"}:${guest.productId}${
      guest.attribute ? `:a:${guest.attribute.id}` : ""
    }`;
    merged.push({
      ...guest,
      key: guest.key || defaultKey,
      orderId: undefined,
      id: undefined,
    });
    indexByIdentity.set(identity, merged.length - 1);
  }

  return clampCartItemQuantities(merged);
}

export function CartProvider({ children }: { children: ReactNode }) {
  const { data: session, status } = useSession();
  const [items, setItems] = useState<CartItem[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const hydratedRef = useRef(false);
  /** When current `items` match this fingerprint, debounced PATCH is unnecessary (e.g. just hydrated). */
  const lastSyncedCartFingerprintRef = useRef<string | undefined>(undefined);
  const syncTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const statusRef = useRef(status);
  statusRef.current = status;

  const customerId = session?.user?.id ?? "";

  const billingDetails = (session?.user as { billing_details?: { currency?: string } } | undefined)?.billing_details;
  const currency = (billingDetails?.currency as string | undefined) ?? "USD";

  const openCart = useCallback(() => setIsOpen(true), []);
  const closeCart = useCallback(() => setIsOpen(false), []);

  // Open cart after login redirect (`/?cart=1`) and strip the query param.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("cart") !== "1") return;
    setIsOpen(true);
    params.delete("cart");
    const next = params.toString();
    const url = `${window.location.pathname}${next ? `?${next}` : ""}${window.location.hash}`;
    window.history.replaceState(null, "", url);
  }, []);

  const persistGuestCart = useCallback((nextItems: CartItem[]) => {
    if (typeof window === "undefined") return;
    try {
      sessionStorage.setItem(GUEST_CART_KEY, JSON.stringify(nextItems));
    } catch {
      // ignore
    }
  }, []);

  const loadGuestCart = useCallback(() => {
    const parsed = readGuestCartFromStorage();
    if (parsed.length > 0) {
      setItems(parsed);
      itemsRef.current = parsed;
    }
  }, []);

  const persistGuestCartIfNeeded = useCallback(
    (nextItems: CartItem[]) => {
      if (statusRef.current !== "authenticated") {
        persistGuestCart(nextItems);
      }
    },
    [persistGuestCart],
  );

  const runCartSync = useCallback(
    async (nextItems: CartItem[]): Promise<CartItem[]> => {
      if (!customerId) {
        return nextItems;
      }
      if (nextItems.length === 0) {
        return nextItems;
      }
      setSyncing(true);
      try {
        // Sync strategy:
        // - For existing backend orders: group items by orderId and update each order separately.
        // - For new items without orderId: group by business_id and create new cart orders.

        const byOrderId = new Map<string, CartItem[]>();
        const newByBusiness = new Map<string, CartItem[]>();
        for (const i of nextItems) {
          if (!i.businessId) continue;
          if (i.orderId) {
            const arr = byOrderId.get(i.orderId) ?? [];
            arr.push(i);
            byOrderId.set(i.orderId, arr);
          } else {
            const arr = newByBusiness.get(i.businessId) ?? [];
            arr.push(i);
            newByBusiness.set(i.businessId, arr);
          }
        }

        const patchPromises = [...byOrderId.entries()].map(
          async ([orderId, orderItems]) => {
            const products = itemsToOrderProducts(orderItems);
            const total_price = itemsTotalCents(orderItems);
            const result = await updateCartOrder({
              id: orderId,
              products,
              total_price,
              currency,
              status: "cart",
              delivery_details: [],
            });
            return result;
          },
        );

        const createPromises = [...newByBusiness.entries()].map(
          async ([bizId, bizItems]) => {
            const products = itemsToOrderProducts(bizItems);
            const total_price = itemsTotalCents(bizItems);
            const res = await createCartOrder({
              business_id: bizId,
              customer_id: customerId,
              products,
              total_price,
              currency,
              status: "cart",
              delivery_details: [],
            });
            const orderId = orderIdFromCreateResult(res);
            return { bizId, orderId, unauthorized: res.unauthorized };
          },
        );

        async function pullAndMerge(local: CartItem[]): Promise<CartItem[]> {
          const { orders, unauthorized } = await getCartOrder();
          if (unauthorized) {
            handleUnauthorizedSession();
            return local;
          }
          const serverLines = clampCartItemQuantities(ordersToItems(orders));
          if (serverLines.length === 0) {
            return local;
          }
          const { merged, changed } = mergeOrderIdsFromServerLines(
            local,
            serverLines,
          );
          if (changed) setItems(merged);
          return merged;
        }

        if (patchPromises.length > 0 || createPromises.length > 0) {
          const [patchResults, createResults] = await Promise.all([
            Promise.all(patchPromises),
            createPromises.length > 0
              ? Promise.all(createPromises)
              : Promise.resolve(
                  [] as {
                    bizId: string;
                    orderId: string | undefined;
                    unauthorized?: boolean;
                  }[],
                ),
          ]);
          for (const pr of patchResults) {
            if (pr?.unauthorized) {
              handleUnauthorizedSession();
              return nextItems;
            }
          }
          for (const cr of createResults) {
            if (cr.unauthorized) {
              handleUnauthorizedSession();
              return nextItems;
            }
          }
          const createdMap = new Map<string, string>();
          for (const { bizId, orderId } of createResults) {
            if (orderId) createdMap.set(bizId, orderId);
          }
          const { merged: withCreated, changed: createdChanged } =
            mergeCreatedOrderIds(nextItems, createdMap);
          if (createdChanged) setItems(withCreated);
          const merged = await pullAndMerge(withCreated);
          touchCartTtl(customerId);
          return merged;
        }

        const merged = await pullAndMerge(nextItems);
        touchCartTtl(customerId);
        return merged;
      } catch (err) {
        console.error("Failed to sync cart:", err);
        return nextItems;
      } finally {
        setSyncing(false);
      }
    },
    [customerId, currency],
  );

  const flushCartSync = useCallback(async () => {
    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current);
      syncTimeoutRef.current = null;
    }
    if (status !== "authenticated" || !customerId) {
      return itemsRef.current;
    }
    const out = await runCartSync(itemsRef.current);
    lastSyncedCartFingerprintRef.current = cartItemsSyncFingerprint(out);
    return out;
  }, [status, customerId, runCartSync]);

  // Restore guest cart from storage on first paint (e.g. after full-page redirect during login).
  useEffect(() => {
    if (itemsRef.current.length > 0) return;
    loadGuestCart();
  }, [loadGuestCart]);

  useEffect(() => {
    if (status !== "authenticated" || !customerId) {
      if (status === "unauthenticated") {
        hydratedRef.current = false;
      }
      if (
        (status === "unauthenticated" || status === "loading") &&
        itemsRef.current.length === 0
      ) {
        loadGuestCart();
      }
      return;
    }
    if (hydratedRef.current) return;
    hydratedRef.current = true;
    let cancelled = false;

    void (async () => {
      try {
        const { orders, unauthorized } = await getCartOrder();
        if (cancelled) return;
        if (unauthorized) {
          handleUnauthorizedSession();
          return;
        }

        const now = Date.now();
        const localTouch = readCartTouchedAt(customerId);
        const freshOrders: ApiCartOrder[] = [];
        const expiredIds: string[] = [];
        for (const order of orders) {
          const id = normalizeOrderIdForCart(order.id);
          if (
            id &&
            isCartOrderExpired(order as Record<string, unknown>, now, localTouch)
          ) {
            expiredIds.push(id);
          } else {
            freshOrders.push(order);
          }
        }
        if (expiredIds.length > 0) {
          await Promise.all(
            expiredIds.map((id) =>
              updateCartItemStatus({ id, status: "deleted" }),
            ),
          );
          if (cancelled) return;
        }

        const guestItems = readGuestCartFromStorage();
        const fromApi = clampCartItemQuantities(ordersToItems(freshOrders));
        const merged = mergeGuestCartWithServer(fromApi, guestItems);
        if (merged.length > 0) {
          lastSyncedCartFingerprintRef.current = undefined;
          setItems(merged);
          itemsRef.current = merged;
        } else if (expiredIds.length > 0 && guestItems.length === 0) {
          clearCartTtl(customerId);
        }

        // Sliding TTL: on login, keep valid carts and refresh by syncing to the server.
        const shouldRefreshTtl =
          freshOrders.length > 0 || guestItems.length > 0;
        if (shouldRefreshTtl && merged.length > 0) {
          touchCartTtl(customerId);
          try {
            const synced = await runCartSync(merged);
            if (cancelled) return;
            lastSyncedCartFingerprintRef.current =
              cartItemsSyncFingerprint(synced);
            touchCartTtl(customerId);
            if (guestItems.length > 0 && typeof window !== "undefined") {
              sessionStorage.removeItem(GUEST_CART_KEY);
            }
          } catch {
            if (!cancelled && guestItems.length > 0) {
              persistGuestCart(merged.length > 0 ? merged : guestItems);
            }
          }
        }
      } catch {
        if (!cancelled) loadGuestCart();
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [status, customerId, loadGuestCart, runCartSync, persistGuestCart]);

  useEffect(() => {
    if (status !== "authenticated" || !customerId) {
      if (status === "unauthenticated" && items.length > 0) persistGuestCart(items);
      return;
    }
    if (items.length === 0) {
      lastSyncedCartFingerprintRef.current = undefined;
      return;
    }
    const fp = cartItemsSyncFingerprint(items);
    if (fp === lastSyncedCartFingerprintRef.current) {
      return;
    }
    if (syncTimeoutRef.current) clearTimeout(syncTimeoutRef.current);
    syncTimeoutRef.current = setTimeout(() => {
      syncTimeoutRef.current = null;
      void (async () => {
        const snap = itemsRef.current;
        if (snap.length === 0) {
          lastSyncedCartFingerprintRef.current = undefined;
          return;
        }
        const snapFp = cartItemsSyncFingerprint(snap);
        if (snapFp === lastSyncedCartFingerprintRef.current) return;
        const out = await runCartSync(snap);
        lastSyncedCartFingerprintRef.current = cartItemsSyncFingerprint(out);
      })();
    }, 500);
    return () => {
      if (syncTimeoutRef.current) clearTimeout(syncTimeoutRef.current);
    };
  }, [items, status, customerId, persistGuestCart, runCartSync]);

  const addItem = useCallback(
    (item: AddCartItemInput, quantity = 1) => {
      setItems((prev) => {
        const next = appendCartLineToItems(prev, item, quantity);
        if (next == null) return prev;
        persistGuestCartIfNeeded(next);
        return next;
      });
    },
    [persistGuestCartIfNeeded],
  );

  const addItemAndSyncToServer = useCallback(
    async (item: AddCartItemInput, quantity = 1) => {
      const prev = itemsRef.current;
      const next = appendCartLineToItems(prev, item, quantity);
      if (next == null) {
        return;
      }
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
        syncTimeoutRef.current = null;
      }
      setItems(next);
      itemsRef.current = next;
      if (status === "authenticated" && customerId) {
        const out = await runCartSync(next);
        lastSyncedCartFingerprintRef.current = cartItemsSyncFingerprint(out);
      } else {
        persistGuestCartIfNeeded(next);
      }
    },
    [status, customerId, runCartSync, persistGuestCartIfNeeded],
  );

  const updateQuantity = useCallback(
    (itemKey: string, quantity: number) => {
      const existing = items.find((i) => i.key === itemKey);

      if (quantity >= 1) {
        const cap = maxQuantityForCartLine(existing?.maxQuantity);
        const capped =
          cap != null ? Math.min(quantity, cap) : quantity;
        const nextItems = items.map((i) =>
          i.key === itemKey ? { ...i, quantity: capped } : i,
        );
        setItems(nextItems);
        persistGuestCartIfNeeded(nextItems);
        if (status === "authenticated") {
          void runCartSync(nextItems).then((out) => {
            lastSyncedCartFingerprintRef.current = cartItemsSyncFingerprint(out);
          });
        }
        return;
      }

      // Treat quantity < 1 as a remove, with special rules for multi-product orders.
      const orderIdNorm = normalizeOrderIdForCart(existing?.orderId);
      const remainingInOrder = countOtherLinesInSameOrder(
        items,
        orderIdNorm,
        itemKey,
      );

      const nextItems = items.filter((i) => i.key !== itemKey);

      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
        syncTimeoutRef.current = null;
      }
      if (status === "authenticated" && orderIdNorm && remainingInOrder === 0) {
        lastSyncedCartFingerprintRef.current = cartItemsSyncFingerprint(nextItems);
      }
      setItems(nextItems);
      persistGuestCartIfNeeded(nextItems);

      if (status === "authenticated") {
        if (orderIdNorm && remainingInOrder === 0) {
          void updateCartItemStatus({ id: orderIdNorm, status: "deleted" });
        } else {
          void runCartSync(nextItems).then((out) => {
            lastSyncedCartFingerprintRef.current = cartItemsSyncFingerprint(out);
          });
        }
      }
    },
    [items, status, runCartSync, persistGuestCartIfNeeded],
  );

  const removeItem = useCallback(
    (itemKey: string) => {
      const existing = items.find((i) => i.key === itemKey);
      const orderIdNorm = normalizeOrderIdForCart(existing?.orderId);
      const remainingInOrder = countOtherLinesInSameOrder(
        items,
        orderIdNorm,
        itemKey,
      );

      const nextItems = items.filter((i) => i.key !== itemKey);

      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
        syncTimeoutRef.current = null;
      }
      if (status === "authenticated" && orderIdNorm && remainingInOrder === 0) {
        lastSyncedCartFingerprintRef.current = cartItemsSyncFingerprint(nextItems);
      }
      setItems(nextItems);
      persistGuestCartIfNeeded(nextItems);

      if (status === "authenticated") {
        if (orderIdNorm && remainingInOrder === 0) {
          void updateCartItemStatus({ id: orderIdNorm, status: "deleted" });
        } else {
          void runCartSync(nextItems).then((out) => {
            lastSyncedCartFingerprintRef.current = cartItemsSyncFingerprint(out);
          });
        }
      }
    },
    [items, status, runCartSync, persistGuestCartIfNeeded],
  );

  const clearCart = useCallback((options?: { skipServerDelete?: boolean }) => {
    if (
      status === "authenticated" &&
      items.length > 0 &&
      !options?.skipServerDelete
    ) {
      const orderIdsUnique = Array.from(
        new Set(items.map((i) => i.orderId).filter(Boolean)),
      ) as string[];
      void Promise.all(
        orderIdsUnique.map((orderId) =>
          updateCartItemStatus({ id: orderId, status: "deleted" }),
        ),
      );
    }
    setItems([]);
    setIsOpen(false);
    if (typeof window !== "undefined") sessionStorage.removeItem(GUEST_CART_KEY);
    if (customerId) clearCartTtl(customerId);
  }, [status, items, customerId]);

  const totalCount = useMemo(
    () => items.reduce((sum, i) => sum + i.quantity, 0),
    [items]
  );

  const totalPrice = useMemo(() => {
    const total = items.reduce(
      (sum, i) =>
        sum +
        (i.unitBasePrice != null ? i.unitBasePrice : parsePrice(i.price)) * i.quantity,
      0
    );
    return formatPrice(total);
  }, [items]);

  const value = useMemo(
    () => ({
      items,
      isOpen,
      openCart,
      closeCart,
      addItem,
      addItemAndSyncToServer,
      updateQuantity,
      removeItem,
      clearCart,
      totalCount,
      totalPrice,
      syncing,
      flushCartSync,
    }),
    [
      items,
      isOpen,
      openCart,
      closeCart,
      addItem,
      addItemAndSyncToServer,
      updateQuantity,
      removeItem,
      clearCart,
      totalCount,
      totalPrice,
      syncing,
      flushCartSync,
    ]
  );

  return (
    <CartContext.Provider value={value}>{children}</CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}
